"use client";

import "./imessage.css";

import { cn } from "@repo/ui/lib/utils";
import { CornerDownRight, Link as LinkIcon, Paperclip } from "lucide-react";
import Link from "next/link";
import { Fragment, useCallback, useEffect, useRef } from "react";

import { AttachmentImage } from "./attachment-image";
import { EmailBody } from "./email-body";

export interface ThreadMessage {
  id: string;
  role: string;
  /**
   * Null for a message whose whole content is its attachment: Linq writes those
   * with no text part at all. The bubble is skipped entirely rather than drawn
   * empty, so the picture stands on its own the way it was sent.
   */
  body: string | null;
  channel: string;
  createdAt: Date | string;
  /** Present on the global thread, absent inside a single project's room. */
  studyId?: string | null;
  studyName?: string | null;
  /** Shown but not yet confirmed by the server. */
  pending?: boolean;
  /** Images sent with this message, absent when there are none. */
  images?: { url: string; mimeType: string }[];
  /**
   * What the message carries besides its words and its pictures: a link that
   * went out, or a file no <img> can draw. A phone message can be nothing but
   * one of these, and then this is the only thing on the row.
   */
  links?: { url: string; label?: string }[];
  /** The id the browser gave it, when it had one. Null on anything else. */
  clientId?: string | null;
  /**
   * Who said it, shown under this bubble when the thread signs every message
   * rather than only the last. Ignored in `thread` mode.
   */
  author?: string;
  /**
   * The mail as it was actually rendered, where the row kept one.
   *
   * Mail only, and mail is where `body` stops being the message: it is the
   * template's plain-text fallback, footers and tracking urls and all. When
   * this is here it replaces the bubble entirely rather than sitting beside it,
   * because the two are the same message and one of them is unreadable.
   */
  bodyHtml?: string | null;
  /**
   * The message this one answers, named above the bubble.
   *
   * Mail is the only surface that carries the fact -- `metadata.email.inReplyTo`
   * names a specific `Message-ID` rather than just the thread -- and drawing it
   * is what keeps an answer from reading as the next thing said instead of as
   * the reply it is. Absent everywhere else, where the bubble above is always
   * what a message is answering.
   */
  replyTo?: string;
}

/**
 * OPERATOR and AGENT are one voice from this side of the thread, which is the
 * same reason turn history maps both to `assistant`.
 */
function isFromUs(role: string): boolean {
  return role === "AGENT" || role === "OPERATOR";
}

function asDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value);
}

/**
 * A run separator stamps the day and the time, not one or the other: "Today
 * 9:41 AM", and a weekday once it is older than that.
 */
function separatorLabel(value: Date | string): string {
  const date = asDate(value);
  const now = new Date();
  const time = date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
  const days = Math.round(
    (new Date(now.toDateString()).getTime() -
      new Date(date.toDateString()).getTime()) /
      86_400_000,
  );
  if (days === 0) {
    return `Today ${time}`;
  }
  if (days === 1) {
    return `Yesterday ${time}`;
  }
  if (days < 7) {
    return `${date.toLocaleDateString([], { weekday: "long" })} ${time}`;
  }
  return `${date.toLocaleDateString([], { month: "short", day: "numeric" })} ${time}`;
}

/**
 * How long ago the last answer landed. Local rather than the shared
 * `RelativeTime`, which paints an absolute UTC string until its effect runs --
 * one frame of "2026-08-27 17:13 UTC" under a bubble in a panel that has just
 * animated open.
 */
function agoLabel(value: Date | string): string {
  const minutes = Math.round((Date.now() - asDate(value).getTime()) / 60_000);
  if (minutes < 1) {
    return "Just now";
  }
  const format = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  if (minutes < 60) {
    return format.format(-minutes, "minute");
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return format.format(-hours, "hour");
  }
  return format.format(-Math.round(hours / 24), "day");
}

/** The pictures the attachment route accepts, for every picker that feeds it. */
export const IMAGE_ATTACHMENT_TYPES =
  "image/png,image/jpeg,image/webp,image/heic,image/heif";

/** A run breaks with a timestamp when the gap is long enough to matter. */
const SEPARATOR_GAP_MS = 60 * 60 * 1000;

/** Global, for splitting. */
const URL_SPLIT = /(https?:\/\/[^\s<]+)/g;
/** Non-global twin for testing: `.test` on a /g regex is stateful and would
    make the same string match on one call and miss on the next. */
const URL_TEST = /^https?:\/\/[^\s<]+$/;

/**
 * Render URLs as links.
 *
 * Deliberately not a markdown renderer: bubbles carry what a person typed, and
 * running that through markdown would turn stray underscores and asterisks in
 * ordinary prose into formatting nobody asked for.
 */
/**
 * The first link in a message, or null.
 *
 * One card per message rather than one per link: a bubble is 25rem wide, and a
 * message carrying four urls would become four cards and no conversation.
 */
export function firstLink(body: string): string | null {
  return new RegExp(URL_SPLIT.source).exec(body)?.[0] ?? null;
}

/** The longest label that still sits on one or two lines in a bubble. */
const MAX_LABEL = 45;

/**
 * What a link reads as in a bubble.
 *
 * The href stays whole -- this only changes what is printed. A shared url is
 * mostly scheme and tracking parameters, none of which anybody reads, and
 * printing them in full is what makes a bubble wrap three times to say
 * "terac.com". Host plus a trimmed path is the part that tells you where you
 * are going.
 *
 * Non-throwing: whatever arrives is text somebody typed, and a label is a
 * display concern that must never take the render down.
 */
export function linkLabel(href: string): string {
  let parsed: URL;
  try {
    parsed = new URL(href);
  } catch {
    return href;
  }
  const host = parsed.hostname.replace(/^www\./, "");
  const path = parsed.pathname.replace(/\/$/, "");
  const full = `${host}${path}`;
  return full.length > MAX_LABEL
    ? `${full.slice(0, MAX_LABEL - 1)}\u2026`
    : full;
}

function renderBody(body: string) {
  const parts = body.split(URL_SPLIT);
  return parts.map((part, index) =>
    URL_TEST.test(part) ? (
      <a
        key={`${part}-${String(index)}`}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
      >
        {linkLabel(part)}
      </a>
    ) : (
      <Fragment key={`t-${String(index)}`}>{part}</Fragment>
    ),
  );
}

export function MessageThread({
  messages,
  agentTyping,
  showProjectLabels = false,
  emptyState,
  onReachTop,
  attribution,
  attributionMode = "thread",
  perspective = "expert",
  renderLinkPreview,
}: {
  messages: ThreadMessage[];
  agentTyping: boolean;
  /** Global thread only: a room already knows which project it is. */
  showProjectLabels?: boolean;
  emptyState: React.ReactNode;
  /**
   * How a link in a message is previewed. Absent renders no card at all, which
   * is what keeps this file free of anything that fetches: the caller owns the
   * request, this owns the layout.
   */
  /**
   * The card for the first link in a message. The boolean is the side the
   * bubble is on for THIS reader, not who sent it: the two disagree in the
   * widget, where the expert reads their own chat.
   */
  renderLinkPreview?: (url: string, ownSide: boolean) => React.ReactNode;
  /**
   * Asked for older messages. Handed the scroller so the caller can hold the
   * reader's place while it prepends -- this element is the only one that
   * scrolls, and wrapping it in another breaks the scroll-to-bottom below.
   */
  onReachTop?: (scroller: HTMLDivElement) => void;
  /** Who the last answer came from, shown under it in `thread` mode. */
  attribution?: string;
  /**
   * Where the signature goes.
   *
   * `thread` signs once, under the last answer, which is what a person talking
   * to one assistant wants: a name under every bubble in a conversation with a
   * single voice is noise.
   *
   * `message` signs each of our bubbles with `message.author`, because on our
   * side of the thread they are not one voice. Sophia and a teammate answering
   * in her place look identical to the expert on purpose, and telling them
   * apart is the whole reason somebody opens the internal viewer.
   */
  attributionMode?: "thread" | "message";
  /**
   * Which side of the thread the reader is on, which decides which bubbles get
   * the sent styling and the right-hand edge.
   *
   * `expert` is the widget: the person sees their own words as sent and the
   * agent's as received. `team` is the internal viewer, where the reader is on
   * our side and the same rows have to flip -- an operator whose own reply
   * appeared on the received side would be reading the conversation backwards
   * from the one they are about to write into.
   */
  perspective?: "expert" | "team";
}) {
  const endRef = useRef<HTMLDivElement>(null);

  // An image or a link card can finish loading after the messages render and
  // grow the thread under the reader, so the pin is re-applied on the next
  // frame as well as this one.
  const scrollerRef = useRef<HTMLDivElement>(null);
  /** Whether the reader is at the bottom, so a resize knows to follow them. */
  const pinnedRef = useRef(true);

  /**
   * `smooth` follows an answer arriving. The first landing never is: animating
   * up from the top of a thread on open is a scroll the reader did not ask for
   * and has to sit through before they can read anything.
   *
   * Still assigning the offset in the instant case rather than `scrollIntoView`,
   * which aligns to the container's content edge and so stopped 16px short --
   * the scroller's own bottom padding -- leaving the last bubble clipped.
   */
  const scrollToBottom = useCallback((smooth = false) => {
    const el = scrollerRef.current;
    if (!el) {
      return;
    }
    // Re-applied on the next frame as well as this one, because an image or a
    // link card can finish loading after the messages render and grow the
    // thread underneath. A smooth scroll retargets rather than restarting, so
    // the second call extends the same movement instead of stuttering it.
    // `behavior: "smooth"` is not covered by the stylesheet's reduced-motion
    // block, which only reaches CSS `scroll-behavior`, so the preference is
    // read here too. Somebody who asked for less movement gets none.
    const wants =
      smooth &&
      !globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const go = () => {
      if (wants) {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      } else {
        el.scrollTop = el.scrollHeight;
      }
    };
    go();
    requestAnimationFrame(go);
  }, []);
  const hasScroller = messages.length > 0 || agentTyping;

  // The first render lands at the bottom; after that it follows only a reader
  // who is already there.
  //
  // The count alone is not enough to decide. Paging prepends older messages, so
  // the list grows without anything arriving at the bottom -- following that
  // throws the reader back to the newest message the instant their history
  // loads, which makes paged history unreadable. `pinnedRef` is the thing that
  // separates "more has arrived below you" from "more has appeared above you".
  //
  // Assigning the offset rather than `scrollIntoView`, which aligns to the
  // container's content edge and so stopped 16px short -- the scroller's own
  // bottom padding -- leaving the last bubble clipped on every open.
  const hasLandedRef = useRef(false);
  useEffect(() => {
    if (!scrollerRef.current) {
      return;
    }
    if (!hasLandedRef.current) {
      hasLandedRef.current = true;
      scrollToBottom();
      return;
    }
    if (pinnedRef.current) {
      scrollToBottom(true);
    }
  }, [messages.length, agentTyping, scrollToBottom]);

  // The panel resizes under the reader when the header's expand toggle fires.
  // Growing keeps the bottom pinned by itself; collapsing strands them.
  //
  // The bubbles are watched as well as the scroller. A link card or a picture
  // that finishes loading grows the thread while the scroller keeps its size,
  // and that growth is what left the newest message below the fold on open.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) {
      return;
    }
    // Instant, unlike an arriving message: the panel is being resized under
    // the reader, and animating on top of that is two movements at once.
    //
    // An observer reports every element once as it starts watching it. For a
    // bubble that has just arrived, that report is the arrival itself, which
    // the effect above already scrolls to smoothly; only later growth counts.
    const sized = new WeakSet<Element>();
    const observer = new ResizeObserver((entries) => {
      let grew = false;
      for (const entry of entries) {
        if (sized.has(entry.target)) {
          grew = true;
        } else {
          sized.add(entry.target);
        }
      }
      if (grew && pinnedRef.current) {
        scrollToBottom();
      }
    });
    observer.observe(el);
    for (const child of el.children) {
      observer.observe(child);
    }
    const arrivals = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node instanceof Element) {
            observer.observe(node);
          }
        }
      }
    });
    arrivals.observe(el, { childList: true });
    return () => {
      observer.disconnect();
      arrivals.disconnect();
    };
  }, [hasScroller, scrollToBottom]);

  if (!hasScroller) {
    return (
      <div className="imsg flex flex-1 items-center justify-center px-6 py-16">
        {emptyState}
      </div>
    );
  }

  // The last message we sent carries the receipt, the way iOS shows Delivered
  // under the most recent outgoing bubble only.
  const lastFromExpertIndex = messages.reduce(
    (found, message, index) => (isFromUs(message.role) ? found : index),
    -1,
  );

  // Its mirror: the most recent answer, and only when it is still the last word
  // in the thread. A signature says who spoke last, so once the reader has
  // replied it belongs to nobody -- leaving it under an older bubble claims
  // Sophia ended a conversation the reader ended.
  const lastIndex = messages.length - 1;
  const signedIndex =
    lastIndex >= 0 && isFromUs(messages[lastIndex]?.role ?? "")
      ? lastIndex
      : -1;

  return (
    <div
      ref={scrollerRef}
      onScroll={() => {
        const el = scrollerRef.current;
        if (!el) {
          return;
        }
        pinnedRef.current =
          el.scrollHeight - el.scrollTop - el.clientHeight < 40;
        if (el.scrollTop < 120) {
          onReachTop?.(el);
        }
      }}
      className="imsg flex flex-1 flex-col overflow-y-auto px-4 py-4"
      aria-live="polite"
      aria-relevant="additions"
    >
      {messages.map((message, index) => {
        // The browser's own id where there is one, because it is the thing that
        // does not change when a pending bubble becomes a stored row. Keyed on
        // `id`, those are two different keys: React unmounts one element and
        // mounts another, `@starting-style` runs again, and your own message
        // flashes as it is confirmed. Falls back to the row id, which is what
        // anything we did not send has.
        const key = message.clientId ?? message.id;
        const previous = messages[index - 1];
        const next = messages[index + 1];
        // Who sent it, and on mail the roles say it differently: an unsigned
        // outbound is `SYSTEM` there rather than `AGENT`, so the plain check
        // would put a notification we sent on the side the expert writes from.
        const fromUs =
          isFromUs(message.role) ||
          (message.channel === "EMAIL" && message.role !== "EXPERT");
        // Grouping and the "who said this" label stay keyed on `fromUs`, which
        // is a fact about the message. Only the side is a question of who is
        // reading.
        const ownSide = perspective === "team" ? fromUs : !fromUs;

        const gapBefore = previous
          ? asDate(message.createdAt).getTime() -
            asDate(previous.createdAt).getTime()
          : Number.POSITIVE_INFINITY;
        const showSeparator = gapBefore > SEPARATOR_GAP_MS;

        // Grouped by side, the way the reference does it, not by side and a
        // one-minute window: an answer whose bubbles land a few minutes apart is
        // still one turn.
        const nextContinuesRun =
          next !== undefined &&
          isFromUs(next.role) === fromUs &&
          next.role !== "SYSTEM" &&
          asDate(next.createdAt).getTime() -
            asDate(message.createdAt).getTime() <=
            SEPARATOR_GAP_MS;
        const startsRun =
          !previous ||
          showSeparator ||
          isFromUs(previous.role) !== fromUs ||
          previous.role === "SYSTEM";

        const showProject =
          showProjectLabels &&
          Boolean(message.studyName) &&
          (startsRun || previous?.studyId !== message.studyId);

        // A `SYSTEM` row is normally the thread's record of something that
        // happened to it, which is a note rather than a message. Mail is the
        // exception and has to be excluded here: an automated send is filed as
        // `SYSTEM` because nobody signed it, and it is still a whole email
        // somebody received. Without this every notification in the inbox
        // rendered as the centred one-liner -- the raw plain-text fallback of
        // an HTML template, footers and tracking urls in one grey paragraph.
        //
        // The exception is written on the channel and not on the presence of
        // `bodyHtml`, which was standing in for it and got the plain-text ones
        // wrong: an automated mail that carries no HTML is still an email, and
        // as a centred note it lost the line naming who sent it -- which is
        // exactly the message a reader cannot otherwise attribute.
        if (message.role === "SYSTEM" && message.channel !== "EMAIL") {
          return (
            <p
              key={key}
              className="text-muted-foreground my-3 text-center text-xs text-balance"
            >
              {message.body}
            </p>
          );
        }

        return (
          <Fragment key={key}>
            {showSeparator ? (
              <p className="text-muted-foreground my-4 text-center text-[11px] font-semibold">
                {separatorLabel(message.createdAt)}
              </p>
            ) : null}

            {showProject && message.studyId ? (
              <Link
                href={`/me/opportunities/${message.studyId}/room`}
                className={cn(
                  "text-muted-foreground hover:text-foreground mt-3 mb-1 max-w-[78%] truncate text-[11px] transition-colors",
                  ownSide ? "self-end" : "self-start",
                )}
              >
                {message.studyName}
              </Link>
            ) : null}

            {/* The only thing that says who spoke is fill colour and which edge
                the bubble sits on, and a screen reader gets neither. Once per
                run, so a turn of four bubbles is announced as one voice. */}
            {startsRun ? (
              <span className="sr-only">
                {fromUs
                  ? "Sophia said"
                  : perspective === "team"
                    ? "The expert said"
                    : "You said"}
              </span>
            ) : null}

            {/* Which mail this answers, drawn above it rather than left to the
                reader to infer from position: a reply is filed under the
                message it names, and in a conversation with four of them the
                one directly above is often not it. */}
            {message.replyTo ? (
              <p
                className={cn(
                  "text-muted-foreground mb-1 flex max-w-[78%] items-center gap-1 text-[11px]",
                  ownSide ? "self-end" : "self-start",
                )}
              >
                <CornerDownRight className="size-3 shrink-0" aria-hidden />
                <span className="truncate">{message.replyTo}</span>
              </p>
            ) : null}

            {/* The picture is the bubble, the way iMessage draws one: padding
                and a background around a photo only make it look like a card.
                A caption keeps its own bubble underneath. */}
            {message.images?.length ? (
              <div
                className={cn(
                  "flex flex-col gap-1",
                  ownSide ? "self-end" : "self-start",
                  message.body
                    ? "mb-1.5"
                    : nextContinuesRun
                      ? "mb-1.5"
                      : "mb-4",
                )}
              >
                {message.images.map((image) => (
                  <AttachmentImage key={image.url} url={image.url} />
                ))}
              </div>
            ) : null}

            {message.bodyHtml ? (
              <EmailBody html={message.bodyHtml} ownSide={ownSide} />
            ) : message.body ? (
              // A row rather than the bubble alone, so the time can sit beside
              // it. The native `title` this replaces was there, and took the
              // browser about a second to show: long enough that everybody
              // reading a thread concluded it was broken.
              <div
                className={cn(
                  // Full width, and the side chosen with `justify-*` rather
                  // than by letting the row shrink to its content. The bubble's
                  // `max-width: 78%` resolves against this element, so a row
                  // sized to fit made the cap 78% of the bubble's own width:
                  // "Or screening" came out three lines tall with the word cut
                  // in half. Against the thread's width it means what it says.
                  "flex w-full",
                  nextContinuesRun ? "mb-1.5" : "mb-4",
                  ownSide ? "justify-end" : "justify-start",
                )}
              >
                <div
                  className={cn(
                    "imsg-bubble",
                    ownSide ? "imsg-sent" : "imsg-received",
                    // A person answering in the agent's place, painted only where
                    // the reader is on our side. To the expert these are one
                    // voice by design; internally telling them apart is the whole
                    // job. The name under the bubble carries the same fact, since
                    // two adjacent hues are not something to make anybody rely on.
                    perspective === "team" &&
                      message.role === "OPERATOR" &&
                      "imsg-operator",
                    // Green for anything that travelled over SMS/iMessage, blue for
                    // what was typed here: the same signal iOS itself uses, so the
                    // channel is legible without a caption under every bubble.
                    // The green SMS tint is an iMessage convention about the
                    // reader's OWN texts, so it only means anything on the
                    // surface where the reader is the person holding the phone.
                    // The internal viewer says the channel in the thread list
                    // instead, and painting our own bubbles green there would be
                    // colour carrying a fact the reader already has.
                    perspective === "expert" &&
                      ownSide &&
                      message.channel === "LINQ" &&
                      "imsg-sms",
                    // Dimming says "this may not have gone through", which is
                    // only true where a send can fail on the way out. In a browser
                    // the message is already on the screen.
                    message.pending &&
                      message.channel === "LINQ" &&
                      "imsg-pending",
                  )}
                >
                  {renderBody(message.body)}
                </div>
              </div>
            ) : null}

            {/* The link the message was, or the file it carried. Its own row
                rather than text inside the bubble: a bodyless message has no
                bubble to put it in, and that is the common case -- most of what
                the agent sends over the phone with no words is an apply link. */}
            {message.links?.length ? (
              <div
                className={cn(
                  // Full width and `justify-*`, the same shape as the bubble
                  // row above: a chip caps itself at `max-w-full`, which
                  // against a row sized to its own content is a cap on itself.
                  "flex w-full flex-wrap gap-1.5",
                  nextContinuesRun ? "mb-1.5" : "mb-4",
                  ownSide ? "justify-end" : "justify-start",
                )}
              >
                {message.links.map((link) => (
                  <a
                    key={link.url}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="border-border bg-muted/40 text-muted-foreground hover:text-foreground inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition-colors"
                  >
                    <LinkIcon className="size-3 shrink-0" />
                    <span className="truncate">{link.label ?? link.url}</span>
                  </a>
                ))}
              </div>
            ) : null}

            {(() => {
              // Never under a rendered mail. The link it would find is the
              // first url in the plain-text fallback -- a footer or a tracking
              // link, not the point of the message -- and the mail already
              // draws its own calls to action.
              const link =
                renderLinkPreview && message.body && !message.bodyHtml
                  ? firstLink(message.body)
                  : null;
              return link ? renderLinkPreview?.(link, ownSide) : null;
            })()}

            {/* Signs the answer, the way a support thread names whoever spoke
                last rather than leaving a bare bubble. In `message` mode every
                one of our bubbles is signed instead, because on our side of the
                thread they are not one voice. */}
            {attributionMode === "message" ? (
              (() => {
                // Three rules, and they answer different questions.
                //
                // A person who stepped in is named wherever they wrote: that is
                // what somebody opened this screen to find, and it is rarely the
                // last row.
                //
                // Mail is named everywhere, whoever sent it. A thread where a
                // colleague's message says who wrote it and the automated one
                // above says nothing reads as a message missing its sender
                // rather than as a different kind of message -- and a rendered
                // HTML card with no line under it is exactly where that gap
                // showed. The server decides what the line says; `role` is what
                // separates "a person wrote this" from "the platform sent it".
                //
                // A time appears once, under whatever spoke last. A stamp on
                // every bubble is a column of numbers nobody reads, and the only
                // one that matters is how long this has been sitting.
                //
                // And when the last word is ours, it takes both, because "who
                // said it and when" is one fact about the state of the thread.
                const isLast = index === messages.length - 1;
                const named =
                  message.role === "OPERATOR" ||
                  message.channel === "EMAIL" ||
                  (isLast && ownSide);
                const parts = [
                  named ? message.author : null,
                  isLast ? agoLabel(message.createdAt) : null,
                ].filter(Boolean);
                return parts.length ? (
                  <p
                    className={cn(
                      "text-muted-foreground mt-[-8px] mb-4 text-xs",
                      ownSide ? "self-end" : "self-start",
                    )}
                  >
                    {parts.join(" \u00b7 ")}
                  </p>
                ) : null;
              })()
            ) : attribution && index === signedIndex ? (
              <p className="text-muted-foreground mt-[-8px] mb-4 self-start text-xs">
                {attribution} &middot; {agoLabel(message.createdAt)}
              </p>
            ) : null}

            {/* A receipt answers "did this reach them", which only a channel
                that can fail to deliver has to answer. On a surface where the
                message is already on the screen it is telephony language
                describing nothing, so the phone line keeps it and the browser
                does not. */}
            {/* And only where the reader is the recipient. To somebody
             * reading the thread from our side it describes nothing they were
             * waiting on. */}
            {perspective === "expert" &&
            message.channel === "LINQ" &&
            index === lastFromExpertIndex &&
            !message.pending ? (
              <p className="text-muted-foreground mb-2 self-end text-[11px]">
                Delivered by text
              </p>
            ) : null}
          </Fragment>
        );
      })}

      {agentTyping ? (
        <div
          className="imsg-bubble imsg-received imsg-typing mb-4"
          role="status"
          aria-label="Sophia is typing"
        >
          <span />
          <span />
          <span />
        </div>
      ) : null}

      <div ref={endRef} />
    </div>
  );
}

/**
 * The composer: a bordered box with the field on top and its controls beneath,
 * which is the shape the reference uses.
 *
 * There is no icon row under the field. The reference carries a paperclip, an
 * emoji picker, a GIF button and a microphone, and this thread has a backend for
 * none of them -- `/api/chat` takes text and the stored row has no attachment.
 * Four permanently dead controls read as a broken product, not as parity.
 */
export interface ComposerAttachment {
  id: string;
  name: string;
  /** A local object url while it uploads, the stored one once it lands. */
  previewUrl: string;
  /** Absent while the upload is still in flight. */
  url?: string;
  mimeType?: string;
  failed?: boolean;
}

export function MessageComposer({
  value,
  onChange,
  onSend,
  disabled,
  placeholder,
  inputRef,
  footnote,
  header,
  field,
  sendBlocked = false,
  attachments = [],
  onAttachFiles,
  onRemoveAttachment,
  voice = "agent",
  sendLabel,
  beforeSend,
}: {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  disabled: boolean;
  placeholder: string;
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  footnote?: string;
  /**
   * A row above the field, inside the same box: a subject line, on the one
   * surface that sends something with one.
   *
   * Inside the border rather than stacked above it because the border is what
   * says "this is one message". A subject in its own box beside the composer
   * would read as a setting for the thread instead of as part of what is about
   * to leave.
   */
  header?: React.ReactNode;
  /**
   * A field of the caller's own in place of the plain textarea.
   *
   * Mail is the surface that needs one: the expert reads a rendered document,
   * so the person writing it has to be able to bold a word or drop a link the
   * way they would in Gmail, and a textarea cannot show them that. Everything
   * around it is unchanged -- the same box, the same subject row above, the same
   * button saying whose name leaves -- because the gesture has not changed, only
   * what the writer can see while they make it.
   *
   * The caller still owns `value`, so `canSend` below and the send itself read
   * the same string whichever field produced it.
   */
  field?: React.ReactNode;
  /**
   * Refuse the send for a reason the caller knows and this box does not.
   *
   * The button goes dead-looking rather than dead: paired with `footnote`, it
   * says why. Nothing here may light up a control that would fail.
   */
  sendBlocked?: boolean;
  attachments?: ComposerAttachment[];
  /** Absent hides the attach control entirely, for a surface without uploads. */
  onAttachFiles?: (files: File[]) => void;
  onRemoveAttachment?: (id: string) => void;
  /**
   * Which voice this box is about to write in, which decides the focus colour.
   *
   * `agent` is the default and the widget: the person is writing to Sophia, and
   * the box matches the brand blue everything else on that surface uses.
   *
   * `operator` is a teammate answering in her place, and the box takes the same
   * violet their bubble will. The composer is a preview of what pressing Send
   * produces, so it should not be the last thing to say who is talking.
   */
  voice?: "agent" | "operator";
  /**
   * Words on the send control instead of an arrow.
   *
   * The widget keeps the arrow: the person knows who they are and there is one
   * place their message can go. A teammate answering in the agent's place is
   * doing something the button should say out loud, and the last thing they
   * look at before pressing it is the button.
   */
  sendLabel?: string;
  /** A control beside the send button, on its side: a preview, say. */
  beforeSend?: React.ReactNode;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  // A picture on its own is a message. But nothing may be sent while an upload
  // is still in flight: the body would name a file the server cannot fetch yet.
  const uploading = attachments.some(
    (attachment) => !attachment.url && !attachment.failed,
  );
  const ready = attachments.filter((attachment) => attachment.url);
  const canSend =
    (value.trim().length > 0 || ready.length > 0) &&
    !disabled &&
    !uploading &&
    !sendBlocked;

  return (
    // `imsg` for the custom properties, not the bubble styling: the composer
    // sits outside the scroller that carries them, and `voice` below reads one.
    <div className="imsg bg-background px-4 pt-1 pb-4">
      {/* A ring rather than a second border pixel on focus: widening the border
          would reflow the field by 1px under the cursor. */}
      <div
        className={cn(
          "border-border rounded-[12px] border transition-[border-color,box-shadow] duration-150 ease-out focus-within:ring-1 motion-reduce:transition-none",
          voice === "operator"
            ? "focus-within:border-[var(--imsg-operator)] focus-within:ring-[var(--imsg-operator)]"
            : "focus-within:border-marketing-blue focus-within:ring-marketing-blue",
        )}
      >
        {header ? (
          <div className="border-border border-b px-3 py-2.5">{header}</div>
        ) : null}
        {attachments.length > 0 ? (
          <ul className="flex flex-wrap gap-2 px-3 pt-3">
            {attachments.map((attachment) => (
              <li key={attachment.id} className="relative">
                {/* eslint-disable-next-line @next/next/no-img-element -- a
                    local object url, which the optimizer cannot take */}
                <img
                  src={attachment.previewUrl}
                  alt={attachment.name}
                  className={cn(
                    "size-14 rounded-[10px] object-cover transition-opacity duration-150",
                    !attachment.url && "opacity-50",
                    attachment.failed && "opacity-40 grayscale",
                  )}
                />
                {attachment.failed ? (
                  <span className="absolute inset-0 flex items-center justify-center rounded-[10px] text-[10px] font-medium text-red-600">
                    Failed
                  </span>
                ) : null}
                <button
                  type="button"
                  onClick={() => onRemoveAttachment?.(attachment.id)}
                  aria-label={`Remove ${attachment.name}`}
                  className="bg-foreground text-background absolute -top-1.5 -right-1.5 flex size-5 items-center justify-center rounded-full text-xs leading-none"
                >
                  &times;
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {field ?? (
          <>
            <label htmlFor="imsg-composer" className="sr-only">
              {placeholder}
            </label>
            <textarea
              id="imsg-composer"
              ref={inputRef}
              value={value}
              onChange={(event) => {
                onChange(event.target.value);
              }}
              onKeyDown={(event) => {
                // Guard on composing so an IME candidate selection never sends.
                if (
                  event.key === "Enter" &&
                  !event.shiftKey &&
                  !event.nativeEvent.isComposing
                ) {
                  event.preventDefault();
                  // The same `canSend` the button reads. Without it the key is a
                  // second way in that answers to nothing: an upload still in
                  // flight would leave with the message, naming a file the server
                  // cannot fetch yet.
                  if (canSend) {
                    onSend();
                  }
                }
              }}
              placeholder={placeholder}
              rows={1}
              className="placeholder:text-muted-foreground block max-h-32 min-h-[24px] w-full resize-none bg-transparent px-3 pt-3 pb-1 text-[15px] leading-[1.47] focus-visible:outline-none"
            />
          </>
        )}
        <div className="flex items-center justify-between px-2 pb-2">
          {onAttachFiles ? (
            <>
              <input
                ref={fileRef}
                type="file"
                accept={IMAGE_ATTACHMENT_TYPES}
                multiple
                className="hidden"
                onChange={(event) => {
                  const chosen = [...(event.target.files ?? [])];
                  if (chosen.length > 0) {
                    onAttachFiles(chosen);
                  }
                  // Cleared so choosing the same file twice fires again.
                  event.target.value = "";
                }}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={disabled}
                aria-label="Attach an image"
                className="text-muted-foreground hover:text-foreground hover:bg-muted flex size-8 shrink-0 items-center justify-center rounded-full transition-colors disabled:opacity-50"
              >
                <Paperclip className="size-[18px]" aria-hidden />
              </button>
            </>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            {beforeSend}
            <button
              type="button"
              onClick={onSend}
              disabled={!canSend}
              aria-label={sendLabel ?? "Send message"}
              className={cn(
                "flex shrink-0 items-center justify-center transition-colors",
                sendLabel
                  ? "h-8 rounded-full px-3 text-[13px] font-medium"
                  : "size-8 rounded-full",
                canSend
                  ? voice === "operator"
                    ? "bg-[var(--imsg-operator)] text-white"
                    : "bg-marketing-blue text-white"
                  : "bg-muted text-muted-foreground",
              )}
            >
              {sendLabel ?? (
                <svg
                  viewBox="0 0 24 24"
                  className="size-[18px]"
                  aria-hidden="true"
                >
                  <path
                    d="M12 19V5M12 5l-6 6M12 5l6 6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
      {footnote ? (
        <p className="text-muted-foreground mt-2 text-[11px]">{footnote}</p>
      ) : null}
    </div>
  );
}
